import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./ImageMagnifier.module.css";

const DEFAULT_ZOOM = 2.8;
const DEFAULT_LENS_SCALE = 0.28;
const DEFAULT_LENS_MIN = 140;
const DEFAULT_LENS_MAX = 260;

const ImageMagnifier = ({
  src,
  alt,
  className = "",
  imageClassName = "",
  wrapperStyle,
  imageStyle,
  zoom = DEFAULT_ZOOM,
  lensScale = DEFAULT_LENS_SCALE,
  lensMin = DEFAULT_LENS_MIN,
  lensMax = DEFAULT_LENS_MAX,
  centerOnOpen = false,
  isFaceActive = true,
}) => {
  const [lensState, setLensState] = useState(null);
  const [isHovering, setIsHovering] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const wrapperRef = useRef(null);
  const imageRef = useRef(null);
  const imageInfoRef = useRef({ width: 0, height: 0 });

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const buildLensState = (localX, localY) => {
    const image = imageRef.current;
    if (!image) return null;

    const rect = image.getBoundingClientRect();
    if (!rect.width || !rect.height) {
      updateImageDimensions();
      return null;
    }

    const info = imageInfoRef.current || {};
    const naturalW = info.naturalWidth || rect.width;
    const naturalH = info.naturalHeight || rect.height;

    const scale = Math.min(rect.width / naturalW, rect.height / naturalH);
    const displayWidth = naturalW * scale;
    const displayHeight = naturalH * scale;
    const contentOffsetX = Math.max(0, (rect.width - displayWidth) / 2);
    const contentOffsetY = Math.max(0, (rect.height - displayHeight) / 2);

    const resolvedX = clamp(localX, 0, displayWidth);
    const resolvedY = clamp(localY, 0, displayHeight);
    const lensSize = Math.max(lensMin, Math.min(lensMax, Math.min(displayWidth, displayHeight) * lensScale));
    const lensHalf = lensSize / 2;
    const lensX = clamp(resolvedX - lensHalf, 0, Math.max(displayWidth - lensSize, 0));
    const lensY = clamp(resolvedY - lensHalf, 0, Math.max(displayHeight - lensSize, 0));
    const zoomScale = zoom;
    const zoomPaneSize = lensSize * zoomScale;

    return {
      x: resolvedX,
      y: resolvedY,
      lensX,
      lensY,
      lensSize,
      width: displayWidth,
      height: displayHeight,
      naturalWidth: naturalW,
      naturalHeight: naturalH,
      displayWidth,
      displayHeight,
      scale,
      resolvedSrc: image.currentSrc || image.src || src,
      offsetLeft: contentOffsetX,
      offsetTop: contentOffsetY,
      zoomScale,
      zoomPaneSize,
    };
  };

  const centerLens = () => {
    const image = imageRef.current;
    if (!image) return;

    const rect = image.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const centeredState = buildLensState(rect.width / 2, rect.height / 2);
    if (centeredState) {
      setLensState(centeredState);
      setIsHovering(true);
    }
  };

  useEffect(() => {
    setLensState(null);
    setIsHovering(false);
  }, [src]);

  useEffect(() => {
    if (!isFaceActive) {
      setIsHovering(true);
      return;
    }

    if (centerOnOpen) {
      updateImageDimensions();
      centerLens();
    }
  }, [isFaceActive, centerOnOpen]);

  const updateImageDimensions = () => {
    const image = imageRef.current;
    if (!image) return;

    imageInfoRef.current = {
      width: image.offsetWidth || image.clientWidth,
      height: image.offsetHeight || image.clientHeight,
      naturalWidth: image.naturalWidth || image.offsetWidth || image.clientWidth,
      naturalHeight: image.naturalHeight || image.offsetHeight || image.clientHeight,
    };
  };

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const handlePointerMove = (event) => {
    const image = imageRef.current;
    if (!image) return;

    const rect = image.getBoundingClientRect();
    if (!rect.width || !rect.height) {
      updateImageDimensions();
      return;
    }

    // If the pointer is outside the image (e.g. over letterboxed area), hide the lens.
    const cx = event.clientX;
    const cy = event.clientY;
    if (cx < rect.left || cx > rect.right || cy < rect.top || cy > rect.bottom) {
      hideLens();
      return;
    }

    const info = imageInfoRef.current || {};
    const naturalW = info.naturalWidth || rect.width;
    const naturalH = info.naturalHeight || rect.height;

    // Calculate how the image is fitted inside the element when `object-fit: contain` is used.
    const scale = Math.min(rect.width / naturalW, rect.height / naturalH);
    const displayWidth = naturalW * scale;
    const displayHeight = naturalH * scale;
    const contentOffsetX = Math.max(0, (rect.width - displayWidth) / 2);
    const contentOffsetY = Math.max(0, (rect.height - displayHeight) / 2);

    // Pointer coordinates relative to the visible image content (not the full element box)
    const localX = event.clientX - rect.left - contentOffsetX;
    const localY = event.clientY - rect.top - contentOffsetY;

    // If pointer is outside the actual image pixels, hide the lens.
    if (localX < 0 || localX > displayWidth || localY < 0 || localY > displayHeight) {
      hideLens();
      return;
    }

    const centeredLensState = buildLensState(localX, localY);
    if (!centeredLensState) return;

    setIsHovering(true);
    setLensState(centeredLensState);
  };

  const handlePointerEnter = (event) => {
    updateImageDimensions();
    if (centerOnOpen && !lensState) {
      centerLens();
    } else {
      handlePointerMove(event);
    }
  };

  const hideLens = () => {
    setIsHovering(false);
  };

  const isActive = Boolean(lensState);

  const isPortrait = Boolean(lensState && lensState.displayHeight > lensState.displayWidth);
  const portraitShiftPx = useMemo(() => {
    if (!isPortrait || !lensState) return 0;
    // Move the pane left by a fraction of its size for portrait images.
    // Limit the overlap so the pane doesn't cover too much of the image.
    const paneSize = lensState.zoomPaneSize || 0;
    const imgWidth = lensState.displayWidth || 0;
    // Allow overlap up to 35% of the image width, and no more than 75% of pane size.
    const maxOverlapByImage = Math.round(imgWidth * 0.35);
    const maxOverlapByPane = Math.round(paneSize * 0.75);
    const maxAllowed = Math.max(0, Math.min(maxOverlapByImage || Infinity, maxOverlapByPane || Infinity, 320));
    const shift = -Math.round(Math.min(paneSize * 0.55, maxAllowed));
    return shift;
  }, [isPortrait, lensState]);

  const zoomImageStyle = useMemo(() => {
    if (!lensState?.resolvedSrc) {
      return {};
    }

    const zoomScale = lensState.zoomScale || zoom;
    const zoomedWidth = lensState.displayWidth * zoomScale;
    const zoomedHeight = lensState.displayHeight * zoomScale;

    return {
      width: `${zoomedWidth}px`,
      height: `${zoomedHeight}px`,
      transform: `translate(${-lensState.lensX * zoomScale}px, ${-lensState.lensY * zoomScale}px)`,
    };
  }, [lensState, zoom]);

  if (isMobile) {
    return (
      <div className={`${styles.stage} ${className}`} style={wrapperStyle}>
        <img
          src={src}
          alt={alt}
          className={`${styles.image} ${imageClassName}`}
          style={{ ...imageStyle, height: 'auto', maxHeight: '100%' }}
          loading="eager"
          fetchPriority="high"
          decoding="sync"
        />
      </div>
    );
  }

  return (
    <div
      ref={wrapperRef}
      className={`${styles.magnifierFrame} ${isActive ? styles.magnifierFrameActive : ""} ${isPortrait ? styles.magnifierPortrait : ""} ${className}`}
      style={wrapperStyle}
      onPointerEnter={isFaceActive ? handlePointerEnter : undefined}
      onPointerMove={isFaceActive ? handlePointerMove : undefined}
      onPointerLeave={isFaceActive ? hideLens : undefined}
      onPointerUp={isFaceActive ? hideLens : undefined}
      onPointerCancel={isFaceActive ? hideLens : undefined}
    >
      <div className={styles.stage}>
        <img
          ref={imageRef}
          src={src}
          alt={alt}
          className={`${styles.image} ${imageClassName}`}
          style={imageStyle}
          loading="eager"
          fetchPriority="high"
          decoding="sync"
          onLoad={() => {
            updateImageDimensions();
            if (centerOnOpen) {
              centerLens();
            }
          }}
        />

        {isHovering && lensState && (
          <div
            className={styles.magnifierLens}
            style={{
              width: `${lensState.lensSize}px`,
              height: `${lensState.lensSize}px`,
              left: `${lensState.offsetLeft + lensState.lensX}px`,
              top: `${lensState.offsetTop + lensState.lensY}px`,
            }}
            aria-hidden="true"
          />
        )}
      </div>

      <div
        className={`${styles.zoomPane} ${isActive ? styles.zoomPaneActive : ""} ${lensState && !isHovering ? styles.zoomPaneBlack : ""}`}
        aria-hidden={!lensState}
        style={lensState?.zoomPaneSize ? { width: `${lensState.zoomPaneSize}px`, height: `${lensState.zoomPaneSize}px`, transform: isPortrait ? `translateX(${portraitShiftPx}px)` : undefined, zIndex: isPortrait ? 3 : undefined } : undefined}
      >
        {lensState && (
          <div className={styles.zoomSurface}>
            {isHovering && (
              <img
                src={lensState.resolvedSrc}
                alt={`${alt} zoomed`}
                className={styles.zoomImage}
                style={zoomImageStyle}
                draggable="false"
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageMagnifier;
